import {
  Button, Card, ErrorState, Field, Input, LoadingState, Permissions, Row, Stack, Textarea,
  useApi, useI18n, usePermission,
  type ChildHealthResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

type HealthField = Exclude<keyof ChildHealthResponse, 'childId'>;

/** Every field of the record, its label, and whether it takes a paragraph rather than a line. */
const FIELDS: { field: HealthField; label: string; long?: boolean }[] = [
  { field: 'bloodType', label: 'health.bloodType' },
  { field: 'chronicConditions', label: 'health.chronic', long: true },
  { field: 'dietaryRestrictions', label: 'health.dietary', long: true },
  { field: 'regularMedication', label: 'health.regularMedication', long: true },
  { field: 'doctorName', label: 'health.doctor' },
  { field: 'doctorPhone', label: 'health.doctorPhone' },
  { field: 'vaccinationNotes', label: 'health.vaccinations', long: true },
  { field: 'napHabits', label: 'health.napHabits' },
  { field: 'comfortItem', label: 'health.comfortItem' },
  { field: 'toiletTrainingStage', label: 'health.toiletStage' },
  { field: 'notes', label: 'health.notes', long: true },
];

/**
 * The child's standing health record (GAPS 3.10): read by holders of a health permission, and kept by holders of
 * `health.manage` as the family tells the nursery (Q17). Standing data, so no history window and no term lock apply.
 * An emptied field is cleared.
 */
export function HealthRecordCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const canManage = usePermission(Permissions.health.manage);
  const canView = usePermission(Permissions.health.view) || canManage;
  const [draft, setDraft] = useState<Record<HealthField, string> | null>(null);

  const record = useQuery({
    queryKey: ['health', childId],
    queryFn: () => api.health.record(childId),
    enabled: canView,
  });

  const save = useMutation({
    mutationFn: () => api.health.updateRecord(childId, Object.fromEntries(
      Object.entries(draft ?? {}).map(([field, value]) => [field, value.trim()]),
    )),
    onSuccess: async () => {
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['health', childId] });
    },
  });

  if (!canView) return null;

  const open = (data: ChildHealthResponse) => {
    save.reset();
    setDraft(Object.fromEntries(FIELDS.map(({ field }) => [field, data[field] ?? ''])) as Record<HealthField, string>);
  };

  return (
    <Card
      title={t('health.record')}
      hint={t('health.hint')}
      action={canManage && record.data && draft === null && (
        <Button variant="secondary" onClick={() => open(record.data)}>{t('action.edit')}</Button>
      )}
    >
      {record.isPending && <LoadingState />}
      {record.isError && <ErrorState error={record.error} onRetry={() => void record.refetch()} />}

      {record.data && draft === null && (
        <Stack tight>
          {FIELDS.every(({ field }) => !record.data[field]) && <p className="dg-card__hint">{t('health.empty')}</p>}
          {FIELDS.filter(({ field }) => record.data[field]).map(({ field, label }) => (
            <span key={field}><span className="dg-muted">{t(label)}: </span>{record.data[field]}</span>
          ))}
        </Stack>
      )}

      {draft && (
        <Stack>
          {FIELDS.map(({ field, label, long }) => (
            <Field key={field} label={t(label)}>
              {(props) => long
                ? <Textarea {...props} rows={2} value={draft[field]} onChange={(event) => setDraft({ ...draft, [field]: event.target.value })} />
                : <Input {...props} value={draft[field]} onChange={(event) => setDraft({ ...draft, [field]: event.target.value })} />}
            </Field>
          ))}
          {save.isError && <ErrorState error={save.error} />}
          <Row>
            <Button busy={save.isPending} busyLabel={t('action.saving')} onClick={() => save.mutate()}>{t('action.save')}</Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      )}
    </Card>
  );
}
